import {
  CourseRequirementType,
  ICourse,
  ICreateCourseRequirement,
  ICourseRequirement,
  PulseCheckSettings,
} from '@/types/courses';
import { BrandliveEvent } from '@/types/projects.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import Popover from '@ui/popover/popover';
import Text from '@ui/text/text/text';
import { useRef } from 'react';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import CourseRequirementRow from '../course-requirement/course-requirement-row';
import { generateBorderDashSvg } from '@/micros/edit-project/helpers/constants/border-constants';
import useGetColorForProjectThemeComponents from '@/utils/colors/use-get-color-for-project-theme-components';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { ICONS } from '@ui/icons/types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import Icon from '@ui/icons/icon';

type CourseRequirementProps = {
  course: ICourse | null;
  workingEvent: BrandliveEvent | null;
  working: boolean;
  handleEditRequirement: (requirement: ICourseRequirement) => void;
  setRequirementToDelete: (uuid: string) => void;
  canAddRequirement: (
    requirements: ICreateCourseRequirement[] | undefined,
    workingEvent: BrandliveEvent | null
  ) => boolean;
  openRequirementModal: (requirement: ICreateCourseRequirement) => void;
  defaultNewRequirement: ICreateCourseRequirement;
  defaultSettings: (type: CourseRequirementType) => PulseCheckSettings | Record<string, never>;
};

export default function CourseRequirement({
  course,
  workingEvent,
  working,
  handleEditRequirement,
  setRequirementToDelete,
  canAddRequirement,
  openRequirementModal,
  defaultNewRequirement,
  defaultSettings,
}: CourseRequirementProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  const { hex } = useGetColorForProjectThemeComponents({
    colorVariable: EColorVariablesV2.primary,
    contrastAgainstAppBackground: false,
  });

  return (
    <>
      <Container className="flex ai-center">
        <Text className="H5" color="var(--gray-100)">
          Requirements to pass
        </Text>
        <OptionalComponent display={!!course?.requirements?.length}>
          <Text className="padding-4" color="var(--gray-400)">
            {' '}
            • {course?.requirements?.length}
          </Text>
        </OptionalComponent>
      </Container>
      <Container className="flex-column gap-10 width-100-percent radius-12 padding-20" background="var(--gray-900)">
        {course?.requirements?.map((requirement) => (
          <CourseRequirementRow
            key={requirement.uuid}
            requirement={requirement}
            workingEvent={workingEvent}
            onEdit={handleEditRequirement}
            onDelete={setRequirementToDelete}
          />
        ))}

        <OptionalComponent display={canAddRequirement(course?.requirements, workingEvent)}>
          <Popover
            ref={popoverRef}
            width="fit-content"
            popoverWidth="100%"
            renderTriggerButton={() => {
              return (
                <Button
                  height="64px"
                  width="100%"
                  className="flex ai-center jc-center radius-16 cursor-pointer flex ai-center gap-4"
                  style={{
                    backgroundImage: `url(${generateBorderDashSvg(hex)})`,
                  }}
                >
                  <Text className="L2 flex ai-center gap-8" color={getThemeColorCSSVariable()}>
                    <Icon name={ICONS.ADD} size={12} />
                    <span>Add requirement</span>
                  </Text>
                </Button>
              );
            }}
          >
            <Button
              width="100%"
              hoverBackgroundColor="var(--gray-700)"
              className="radius-0"
              disabled={working}
              onClick={() => {
                openRequirementModal({
                  ...defaultNewRequirement,
                  type: CourseRequirementType.watched_minute,
                  settings: defaultSettings(CourseRequirementType.watched_minute),
                });
              }}
            >
              <Text className="S3">Watchtime</Text>
            </Button>
            <Button
              width="100%"
              hoverBackgroundColor="var(--gray-700)"
              className="radius-0"
              disabled={working}
              onClick={() => {
                openRequirementModal({
                  ...defaultNewRequirement,
                  type: CourseRequirementType.pulse_check,
                  settings: defaultSettings(CourseRequirementType.pulse_check),
                });
              }}
            >
              <Text className="S3">Pulse check</Text>
            </Button>
          </Popover>
        </OptionalComponent>
      </Container>
    </>
  );
}
